import {
  SupabaseRemote,
  createBsyncClient,
  devAutoSignIn,
  devEmailSignIn,
  disconnectDevice,
  listDeviceExtensions,
  listDevices,
  registerDevice,
  renameDevice,
  subscribeToChanges,
  type BsyncClient,
  type RealtimeState,
} from '@bsync/client';
import type { DeviceExtension, DeviceRecord, MaterializedTree } from '@bsync/shared';
import { BookmarkEditor, EditError, SyncCore, SyncRunner, asRemoteError, type SyncStatus } from '@bsync/sync-engine';
import type { Session, User } from '@supabase/supabase-js';
import { batch, createMemo, createRoot, createSignal } from 'solid-js';
import { config, isConfigured } from './config';
import { IdbStore } from './idb-store';
import { toast } from './toasts';

const WEB_DEVICE_KEY = (uid: string) => `bsync:web-device:${uid}`;

let client: BsyncClient | null = null;
export function getClient(): BsyncClient {
  if (!client) {
    client = createBsyncClient({
      supabaseUrl: config.supabaseUrl!,
      supabaseAnonKey: config.supabaseAnonKey!,
      detectSessionInUrl: true,
    });
  }
  return client;
}

interface Running {
  user: User;
  deviceId: string;
  core: SyncCore;
  editor: BookmarkEditor;
  runner: SyncRunner;
  stop: () => void;
}

let running: Running | null = null;

/** Global reactive application state. */
export const state = createRoot(() => {
  const [session, setSession] = createSignal<Session | null | undefined>(undefined);
  const [status, setStatus] = createSignal<SyncStatus | null>(null);
  const [version, setVersion] = createSignal(0);
  const [devices, setDevices] = createSignal<DeviceRecord[] | null>(null);
  const [extensions, setExtensions] = createSignal<DeviceExtension[] | null>(null);
  const [realtime, setRealtime] = createSignal<RealtimeState>('connecting');
  const [online, setOnline] = createSignal(typeof navigator === 'undefined' ? true : navigator.onLine);
  const [loadError, setLoadError] = createSignal<string | null>(null);
  const [ready, setReady] = createSignal(false);
  const tree = createMemo<MaterializedTree | null>(() => {
    version();
    return running?.core.tree() ?? null;
  });
  return { session, setSession, status, setStatus, version, setVersion, devices, setDevices, extensions, setExtensions, realtime, setRealtime, online, setOnline, loadError, setLoadError, ready, setReady, tree };
});

export const currentDeviceId = () => running?.deviceId ?? null;
const bump = () => state.setVersion((v) => v + 1);

export async function bootstrap(): Promise<void> {
  if (!isConfigured()) {
    state.setSession(null);
    return;
  }
  const c = getClient();
  c.auth.onAuthStateChange((event, session) => {
    state.setSession(session);
    if (event === 'SIGNED_OUT') void stop();
    else if (session && (!running || running.user.id !== session.user.id)) void start(session.user);
  });
  const { data } = await c.auth.getSession();
  if (!data.session && config.devAutoLogin) {
    // Dev mode: sign in as the shared local dev user; onAuthStateChange starts syncing.
    try {
      await devAutoSignIn(c, config.supabaseUrl);
      return;
    } catch (err) {
      toast(`Dev auto-login failed: ${err instanceof Error ? err.message : String(err)}`, { tone: 'bad' });
    }
  }
  state.setSession(data.session);
  if (data.session) await start(data.session.user);
  window.addEventListener('online', () => {
    state.setOnline(true);
    running?.runner.request(0);
  });
  window.addEventListener('offline', () => state.setOnline(false));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') running?.runner.request(0);
  });
}

async function ensureWebDevice(user: User): Promise<string> {
  const key = WEB_DEVICE_KEY(user.id);
  const existing = localStorage.getItem(key);
  try {
    const rec = await registerDevice(getClient(), {
      name: `Web dashboard (${browserLabel()})`,
      browser: 'web',
      platform: navigator.platform || null,
      userAgent: navigator.userAgent,
      appVersion: '0.1.0',
      existingId: existing,
    });
    localStorage.setItem(key, rec.id);
    return rec.id;
  } catch (err) {
    if (existing && asRemoteError(err).code === 'offline') return existing;
    throw err;
  }
}

let starting: Promise<void> | null = null;

async function start(user: User): Promise<void> {
  if (starting) return starting;
  starting = (async () => {
    state.setLoadError(null);
    try {
      const deviceId = await ensureWebDevice(user);
      const core = new SyncCore({ deviceId, remote: new SupabaseRemote(getClient()), store: new IdbStore(`${user.id}:${deviceId}:`) });
      await core.init();
      let seenConflicts = core.getStatus().conflicts.length;
      core.subscribe((s) => {
        batch(() => {
          state.setStatus(s);
          if (s.conflicts.length > seenConflicts) {
            const n = s.conflicts.length - seenConflicts;
            toast(`${n} conflicting change${n === 1 ? ' was' : 's were'} resolved automatically.`, { tone: 'warn', action: { label: 'Review', href: '/?conflicts=1' } });
          }
          seenConflicts = s.conflicts.length;
        });
      });
      const runner = new SyncRunner(
        async () => {
          await core.sync();
          bump();
        },
        { debounceMs: 400, onError: (err) => void onSyncError(err) },
      );
      const stopRealtime = subscribeToChanges(getClient(), user.id, {
        onSyncChange: () => runner.request(150),
        onDevicesChange: () => void refreshDevices(),
        onExtensionsChange: () => void refreshExtensions(),
        onState: state.setRealtime,
      });
      const periodic = setInterval(() => runner.request(0), 60_000);
      running = {
        user,
        deviceId,
        core,
        editor: new BookmarkEditor(core),
        runner,
        stop: () => {
          runner.stop();
          stopRealtime();
          clearInterval(periodic);
        },
      };
      batch(() => {
        state.setStatus(core.getStatus());
        state.setReady(true);
        bump();
      });
      void runner.run();
      void refreshDevices();
      void refreshExtensions();
    } catch (err) {
      state.setLoadError(asRemoteError(err).message);
    } finally {
      starting = null;
    }
  })();
  return starting;
}

async function stop() {
  running?.stop();
  running = null;
  batch(() => {
    state.setReady(false);
    state.setStatus(null);
    state.setDevices(null);
    state.setExtensions(null);
    bump();
  });
}

async function onSyncError(err: unknown) {
  const e = asRemoteError(err);
  if (e.code === 'device_revoked' && running) {
    // This dashboard session was disconnected (e.g. from the Devices page): start a fresh one.
    const user = running.user;
    await running.core.reset();
    localStorage.removeItem(WEB_DEVICE_KEY(user.id));
    await stop();
    await start(user);
  } else if (e.code === 'unauthorized') {
    const { error } = await getClient().auth.refreshSession();
    if (error) toast('Your session expired. Sign in again.', { tone: 'bad' });
    else running?.runner.request(0);
  }
}

export async function retrySync() {
  await running?.runner.run();
}

export async function refreshDevices() {
  try {
    state.setDevices(await listDevices(getClient()));
  } catch (err) {
    toast(`Could not load devices: ${asRemoteError(err).message}`, { tone: 'bad' });
  }
}

export async function refreshExtensions() {
  try {
    state.setExtensions(await listDeviceExtensions(getClient()));
  } catch (err) {
    toast(`Could not load extensions: ${asRemoteError(err).message}`, { tone: 'bad' });
  }
}

/** Run an edit against the local replica, then sync it. Errors become toasts. */
export async function edit<T>(fn: (editor: BookmarkEditor) => Promise<T>): Promise<T | undefined> {
  if (!running) return undefined;
  try {
    const result = await fn(running.editor);
    bump();
    running.runner.request(300);
    return result;
  } catch (err) {
    toast(err instanceof EditError ? err.message : `Could not save: ${String(err)}`, { tone: 'bad' });
    return undefined;
  }
}

export async function clearConflicts() {
  await running?.core.clearConflicts();
}

export async function renameDeviceById(id: string, name: string) {
  await renameDevice(getClient(), id, name);
  await refreshDevices();
}

export async function disconnectDeviceById(id: string) {
  await disconnectDevice(getClient(), id);
  await refreshDevices();
  await refreshExtensions();
}

export async function signInWithGoogle() {
  const { error } = await getClient().auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${location.origin}/auth/callback`, queryParams: { prompt: 'select_account' } },
  });
  if (error) throw error;
}

export async function devSignIn(email: string, password: string) {
  await devEmailSignIn(getClient(), email, password);
}

export async function signOut() {
  await stop();
  await getClient().auth.signOut({ scope: 'local' });
}

function browserLabel(): string {
  const ua = navigator.userAgent;
  const b = /Firefox\//.test(ua) ? 'Firefox' : /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${b} on ${os}` : b;
}
